import { FacebookIcon } from "./SocialIcons";
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { buildShareIntents } from "@a2z/ui/share/intents";
import { Button } from "@/shared/components/Button";
import { IconButton, Modal, SharePreviewCard } from "@a2z/ui";
import { copyTextToClipboard } from "@/lib/browser/clipboard";
import { useLocale, useTranslations, translateForLocale } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { ShareMenuProps } from "@/shared/components/types/share";

const COPIED_RESET_MS = 1800;

/** The full ready-to-paste post = localized caption, then the link on its own line. */
function buildPost(caption: string, url: string): string {
  return `${caption}\n${url}`;
}

/**
 * Share a storefront link to social channels.
 *
 * ponytail: a ModalSheet (not a hand-rolled anchored dropdown like
 * ProductActionsMenu) — sharing is infrequent, a sheet works on every viewport,
 * is RTL-correct, and needs no portal/positioning math. Channels are the
 * web-intent links from `@a2z/ui/share/intents`; "Copy" opens an editable post
 * (with an EN/AR toggle) to paste anywhere; "More" surfaces the native share
 * sheet (the Instagram/any-app path on mobile) when available.
 */
export function ShareMenu({
  url,
  messageKey,
  messageValues,
  messageValuesEn,
  messageValuesAr,
  title,
  triggerLabel,
  triggerAriaLabel,
  triggerVariant = "outline",
  triggerClassName,
  open: controlledOpen,
  onClose: controlledOnClose,
  elevated,
  preview,
  readiness,
}: ShareMenuProps) {
  const t = useTranslations();
  const locale = useLocale();
  const defaultLocale: "en" | "ar" = locale === "ar" ? "ar" : "en";
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? controlledOpen : internalOpen;
  const [view, setView] = useState<"main" | "compose">("main");
  const [composeLocale, setComposeLocale] = useState<"en" | "ar">(
    defaultLocale,
  );
  const [drafts, setDrafts] = useState<{ en: string; ar: string }>({
    en: "",
    ar: "",
  });
  const [copied, setCopied] = useState(false);
  const [readinessOpen, setReadinessOpen] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), COPIED_RESET_MS);
    return () => window.clearTimeout(timer);
  }, [copied]);

  // Resolve the caption in both languages — the compose view lets the seller
  // switch language and edit the post before copying. Per-locale values keep a
  // localized value (e.g. a product name) in its own language on each side.
  const captionEn = translateForLocale(
    "en",
    messageKey,
    messageValuesEn ?? messageValues,
  );
  const captionAr = translateForLocale(
    "ar",
    messageKey,
    messageValuesAr ?? messageValues,
  );
  const currentCaption = locale === "ar" ? captionAr : captionEn;

  // Channel intents auto-fill in the seller's current UI language.
  const intents = buildShareIntents({ url, text: currentCaption });
  const canNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  // Self-extinguishing: a store with nothing missing gets no note at all, so
  // the reminder cannot become a permanent tax on a merchant who shares their
  // link ten times a week. `ready` keeps a finished store from flashing "5
  // things to check" while the queries are still answering.
  // Derived from the rows actually rendered, so the count in the summary line
  // and the list under it can never disagree.
  const missingRows = (readiness?.items ?? []).filter((item) => !item.done);
  const showReadiness = Boolean(readiness?.ready) && missingRows.length > 0;

  const close = (): void => {
    if (isControlled) controlledOnClose?.();
    else setInternalOpen(false);
    setView("main");
    setCopied(false);
    setReadinessOpen(false);
  };

  const openCompose = (): void => {
    // Seed both language drafts; switching language keeps each side's edits.
    setDrafts({ en: buildPost(captionEn, url), ar: buildPost(captionAr, url) });
    setComposeLocale(defaultLocale);
    setCopied(false);
    setView("compose");
  };

  const handleCopyDraft = async (): Promise<void> => {
    if (await copyTextToClipboard(drafts[composeLocale])) {
      setCopied(true);
    }
  };

  const handleNativeShare = async (): Promise<void> => {
    try {
      await navigator.share({ text: currentCaption, url });
      close();
    } catch {
      // User dismissed the native sheet (or the channel was cancelled) — there
      // is nothing to recover or report. ponytail: a dismissed share is not an error.
    }
  };

  return (
    <>
      {isControlled ? null : triggerLabel ? (
        <Button
          type="button"
          variant={triggerVariant}
          size="sm"
          leadingIcon={<ShareIcon />}
          onClick={() => setInternalOpen(true)}
          aria-label={triggerAriaLabel}
          className={triggerClassName}
        >
          {triggerLabel}
        </Button>
      ) : (
        <IconButton
          type="button"
          variant="outline"
          size="md"
          shape="square"
          icon={<ShareIcon />}
          ariaLabel={triggerAriaLabel}
          onClick={() => setInternalOpen(true)}
          className={triggerClassName}
        />
      )}

      <Modal
        isOpen={open}
        onClose={close}
        elevated={elevated}
        size="sm"
        icon={<ShareIcon />}
        iconTone="muted"
        title={view === "main" ? title : t(keys.share.composeTitle)}
        subtitle={view === "main" ? url : undefined}
        closeLabel={t(keys.common.close)}
        headerLeading={
          view === "compose" ? (
            <button
              type="button"
              onClick={() => setView("main")}
              aria-label={t(keys.share.back)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-[9px] border border-[color:var(--border-light)] bg-[color:var(--surface)] text-[color:var(--text-secondary)] transition hover:bg-[color:var(--hover)]"
            >
              <BackIcon />
            </button>
          ) : undefined
        }
        hideFooter
      >
        {view === "main" ? (
          <div className="flex flex-col gap-2">
            {/* What a stranger arriving from this link would not find. Above
                the channels because this is the last moment it is cheap to
                fix — and a sibling of them, never a wrapper: a merchant who
                wants to send an unfinished store presses WhatsApp and goes.
                It informs, it does not gate. */}
            {showReadiness ? (
              <ReadinessNote
                items={missingRows}
                expanded={readinessOpen}
                onToggle={() => setReadinessOpen((prev) => !prev)}
                summary={t(keys.share.readinessSummary, {
                  count: missingRows.length,
                })}
                hint={t(keys.share.readinessHint)}
                onNavigate={close}
              />
            ) : null}
            {/* The card first, then the channels: the seller is about to send
                this to a customer, and this is the moment they can still fix
                it if the picture or the price is wrong. */}
            {preview ? (
              <div className="mb-1">
                <SharePreviewCard
                  imageUrl={preview.imageUrl}
                  title={preview.title}
                  description={preview.description}
                  host={preview.host}
                  emptyImageLabel={t(keys.share.previewNoImage)}
                  caption={t(keys.share.previewProductCaption)}
                />
              </div>
            ) : null}
            <ShareChannelLink
              href={intents.whatsapp}
              label={t(keys.share.whatsapp)}
              icon={<WhatsAppIcon />}
              onNavigate={close}
            />
            <ShareChannelLink
              href={intents.x}
              label={t(keys.share.x)}
              icon={<XIcon />}
              onNavigate={close}
            />
            <ShareChannelLink
              href={intents.facebook}
              label={t(keys.share.facebook)}
              icon={<FacebookIcon />}
              onNavigate={close}
            />

            {/* "نسخ" copies the link on the tap (B7) — it used to open the
                post editor first, four taps for a link. The editor is one
                row below, named for what it is. */}
            <ShareActionButton
              label={copied && view === "main" ? t(keys.share.copied) : t(keys.share.copyLinkAction)}
              icon={copied && view === "main" ? <CheckIcon /> : <CopyIcon />}
              onClick={() => {
                void copyTextToClipboard(url).then((ok) => {
                  if (ok) setCopied(true);
                });
              }}
            />
            <ShareActionButton
              label={t(keys.share.editText)}
              icon={<EditIcon />}
              onClick={openCompose}
            />

            {canNativeShare ? (
              <ShareActionButton
                label={t(keys.share.more)}
                icon={<MoreIcon />}
                onClick={handleNativeShare}
              />
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="inline-flex rounded-[10px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] p-0.5">
              <LangTab
                active={composeLocale === "en"}
                onClick={() => setComposeLocale("en")}
                label={t(keys.common.languageEnglish)}
              />
              <LangTab
                active={composeLocale === "ar"}
                onClick={() => setComposeLocale("ar")}
                label={t(keys.common.languageArabic)}
              />
            </div>

            <textarea
              value={drafts[composeLocale]}
              onChange={(event) =>
                setDrafts((prev) => ({
                  ...prev,
                  [composeLocale]: event.target.value,
                }))
              }
              // Stop keys (esp. Enter) from bubbling to a clickable parent card
              // (e.g. the product grid card's role="link" keydown handler).
              onKeyDown={(event) => event.stopPropagation()}
              dir={composeLocale === "ar" ? "rtl" : "ltr"}
              rows={5}
              className="w-full resize-none rounded-[12px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] p-3 text-[15px] leading-relaxed text-[color:var(--text-primary)] outline-none transition focus:border-[color:var(--border-strong)]"
            />

            <Button
              type="button"
              variant="primary"
              size="sm"
              leadingIcon={copied ? <CheckIcon /> : <CopyIcon />}
              onClick={handleCopyDraft}
              className="w-full"
            >
              {copied ? t(keys.share.copied) : t(keys.share.copy)}
            </Button>
          </div>
        )}
      </Modal>
    </>
  );
}

/**
 * The readiness reminder: one collapsed line, expandable to the rows.
 *
 * Collapsed by default and capped in height when open, because the channels
 * must stay reachable — on a 360px phone five expanded rows would otherwise
 * push WhatsApp below the fold, which is exactly the wall this must not be.
 */
function ReadinessNote({
  items,
  expanded,
  onToggle,
  summary,
  hint,
  onNavigate,
}: {
  items: { id: string; title: string; href: string }[];
  expanded: boolean;
  onToggle: () => void;
  summary: string;
  hint: string;
  onNavigate: () => void;
}) {
  return (
    // Deliberately the quiet surface, not the warning tint: nothing here is
    // broken and nothing is stopped. A red-orange banner above the send button
    // reads as "you may not", which is the one thing this must never say.
    <div className="mb-1 rounded-[12px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)]">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-start"
      >
        <span className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center text-[color:var(--text-secondary)]">
          <AlertIcon />
        </span>
        <span className="min-w-0 flex-1 text-[13px] font-semibold text-[color:var(--text-primary)]">
          {summary}
        </span>
        <span
          className={`shrink-0 text-[color:var(--text-muted)] transition-transform ${
            expanded ? "rotate-180" : ""
          }`}
        >
          <ChevronIcon />
        </span>
      </button>
      {expanded ? (
        <div className="max-h-[38vh] overflow-y-auto px-3.5 pb-3">
          <ul className="flex flex-col gap-1.5">
            {items.map((item) => (
              <li key={item.id}>
                {/* `Link`, not `<a href>`: every call site of this menu is
                    inside the router, and a bare anchor would reload the whole
                    portal — losing the seller's place, their unsaved edits on
                    the surface behind, and a second of their time — to move
                    one screen. Same treatment as GlobalSearchPanel's rows. The
                    channel links below are genuinely external and stay
                    anchors. */}
                <Link
                  to={item.href}
                  onClick={onNavigate}
                  className="flex items-center gap-2 rounded-[9px] bg-[color:var(--surface)] px-3 py-2 text-[13px] font-medium text-[color:var(--text-primary)] transition hover:bg-[color:var(--hover)]"
                >
                  <span className="min-w-0 flex-1">{item.title}</span>
                  <span className="shrink-0 text-[color:var(--text-muted)] rtl:rotate-180">
                    <ChevronIcon className="h-4 w-4 -rotate-90" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          {/* Said out loud, once, where the rows are: nothing here stops you. */}
          <p className="mt-2 text-[12px] leading-relaxed text-[color:var(--text-secondary)]">
            {hint}
          </p>
        </div>
      ) : null}
    </div>
  );
}

function ShareChannelLink({
  href,
  label,
  icon,
  onNavigate,
}: {
  href: string;
  label: string;
  icon: ReactNode;
  onNavigate: () => void;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onNavigate}
      className="flex items-center gap-3 rounded-[12px] border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3 text-[15px] font-semibold text-[color:var(--text-primary)] transition hover:border-[color:var(--border-strong)] hover:bg-[color:var(--surface-alt)]"
    >
      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center text-[color:var(--text-secondary)]">
        {icon}
      </span>
      {label}
    </a>
  );
}

function ShareActionButton({
  label,
  icon,
  onClick,
  highlighted = false,
}: {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  highlighted?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-3 rounded-[12px] border px-4 py-3 text-start text-[15px] font-semibold transition ${
        highlighted
          ? "border-[color:var(--success-500)] bg-[color:var(--success-100)] text-[color:var(--success-500)]"
          : "border-[color:var(--border-light)] bg-[color:var(--surface)] text-[color:var(--text-primary)] hover:border-[color:var(--border-strong)] hover:bg-[color:var(--surface-alt)]"
      }`}
    >
      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center">
        {icon}
      </span>
      {label}
    </button>
  );
}

function LangTab({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-[8px] px-3 py-1.5 text-[13px] font-semibold transition ${
        active
          ? "bg-[color:var(--surface)] text-[color:var(--text-primary)] shadow-[var(--shadow-sm)]"
          : "text-[color:var(--text-muted)]"
      }`}
    >
      {label}
    </button>
  );
}

// --- Icons (inlined — seller app has no icon library) ---------------------

function ShareIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
      <path d="M12.04 2c-5.46 0-9.9 4.44-9.9 9.9 0 1.74.46 3.45 1.32 4.95L2 22l5.27-1.38a9.9 9.9 0 0 0 4.77 1.22c5.46 0 9.9-4.44 9.9-9.9S17.5 2 12.04 2zm0 18.05c-1.5 0-2.97-.4-4.25-1.16l-.3-.18-3.13.82.84-3.05-.2-.31a8.2 8.2 0 0 1-1.26-4.36c0-4.54 3.7-8.23 8.25-8.23 4.54 0 8.23 3.69 8.23 8.23 0 4.54-3.69 8.24-8.23 8.24zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.25-.64.8-.79.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.5.11-.11.25-.29.37-.43.13-.15.17-.25.25-.42.08-.16.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.42l-.48-.01c-.16 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.16 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.28z" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z" />
    </svg>
  );
}


function CopyIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-[18px] w-[18px] text-[color:var(--text-secondary)]"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-[18px] w-[18px]"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-[18px] w-[18px] text-[color:var(--text-secondary)]"
      fill="currentColor"
    >
      <circle cx="5" cy="12" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="19" cy="12" r="1.8" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-[18px] w-[18px]"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v4.5M12 16h.01" />
    </svg>
  );
}

function ChevronIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function BackIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-5 w-5 rtl:rotate-180"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}
